import styled, { createGlobalStyle } from 'styled-components';
import theme from 'src/theme';

interface StatusButtonProps {
    tone: 'success' | 'warning' | 'error' | 'neutral';
}

interface PopoverHeaderProps {
    tone: 'success' | 'warning' | 'error' | 'neutral';
}

const toneColors = {
    success: theme.colors.status.success,
    warning: theme.colors.status.warning,
    error: theme.colors.status.error,
    neutral: theme.colors.text.lightGrey,
};

export const PopoverGlobalStyles = createGlobalStyle`
    .connection-status-popover {
        --antd-arrow-background-color: ${theme.colors.background.header};

        .ant-popover-inner {
            padding: 0;
            overflow: hidden;
        }

        .ant-popover-title {
            border-bottom: 0;
            margin-bottom: 0;
            min-height: auto;
            padding: 0;
        }

        .ant-popover-inner-content {
            padding: 0;
        }
    }
`;

export const StatusButton = styled.button<StatusButtonProps>`
    align-items: center;
    background: transparent;
    border: 0;
    box-shadow: none;
    box-sizing: border-box;
    color: ${theme.colors.text.primary};
    cursor: pointer;
    display: flex;
    flex-direction: column;
    font: inherit;
    gap: 2px;
    justify-content: center;
    margin: 0;
    min-height: 44px;
    min-width: 4.5rem;
    outline: none;
    padding: 3px 5px;
    user-select: none;

    &:hover {
        background: ${theme.colors.background.hover.default};
        border: 0;
        box-shadow: none;
        outline: none;
    }

    &:active {
        background: ${theme.colors.background.hover.default};
        border: 0;
        box-shadow: none;
        outline: none;
    }

    &:focus {
        border: 0;
        box-shadow: none;
        outline: none;
    }

    &:focus:not(:focus-visible) {
        border: 0;
        box-shadow: none;
        outline: none;
    }

    &:focus-visible {
        border: 0;
        box-shadow: none;
        outline: 2px solid ${theme.colors.border.inputHover};
        outline-offset: -2px;
    }

    &.ant-popover-open {
        border: 0;
        box-shadow: none;
        outline: none;
    }

    &.ant-popover-open:hover,
    &.ant-popover-open:focus:not(:focus-visible) {
        border: 0;
        box-shadow: none;
        outline: none;
    }

    svg {
        color: ${({ tone }) => toneColors[tone]};
    }
`;

export const StatusLabel = styled.span`
    font-size: 0.65rem;
    font-weight: 600;
    text-align: center;
    white-space: nowrap;
`;

export const PopoverHeader = styled.div<PopoverHeaderProps>`
    align-items: center;
    background: ${theme.colors.background.header};
    border-bottom: 1px solid ${theme.colors.border.panel};
    box-sizing: border-box;
    color: ${theme.colors.text.primary};
    display: flex;
    font-size: 0.85rem;
    font-weight: 600;
    gap: 8px;
    justify-content: center;
    line-height: 1.4;
    margin: 0;
    padding: 10px 16px;
    text-align: center;
    width: 100%;

    svg {
        color: ${({ tone }) => toneColors[tone]};
        flex-shrink: 0;
        font-size: 0.95rem;
    }
`;

export const PopoverTitleText = styled.span`
    color: ${theme.colors.text.primary};
    font-weight: 600;
`;

export const ScreenReaderOnly = styled.span`
    border: 0;
    clip: rect(0 0 0 0);
    clip-path: inset(50%);
    height: 1px;
    margin: -1px;
    overflow: hidden;
    padding: 0;
    position: absolute;
    white-space: nowrap;
    width: 1px;
`;

export const Details = styled.div`
    box-sizing: border-box;
    display: grid;
    gap: 8px;
    max-width: 22rem;
    min-width: 16rem;
    padding: 12px 16px;
`;

export const DetailRow = styled.p`
    color: ${theme.colors.text.primary};
    font-size: 0.85rem;
    line-height: 1.4;
    margin: 0;
`;

export const LastUpdateRow = styled.div`
    display: flex;
    gap: 4px;
    font-size: 0.8rem;
    line-height: 1.4;

    span {
        color: ${theme.colors.text.primary};
        font-weight: 500;
    }

    time {
        color: ${theme.colors.text.primary};
        font-weight: 600;
    }
`;

export const NoticeDot = styled.span`
    background-color: ${theme.colors.brand.lightBlue};
    border-radius: 50%;
    display: inline-block;
    flex-shrink: 0;
    height: 7px;
    margin-top: 1px;
    width: 7px;
`;

export const ReconnectNoticeBanner = styled.div`
    background: ${theme.colors.background.header};
    border: 1px solid ${theme.colors.border.panel};
    border-radius: 4px;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin: 4px 0;
    padding: 8px 10px;
`;

export const ReconnectNoticeHeader = styled.div`
    align-items: center;
    color: ${theme.colors.text.primary};
    display: flex;
    font-size: 0.85rem;
    font-weight: 600;
    gap: 6px;

    svg {
        color: ${theme.colors.brand.lightBlue};
        font-size: 0.8rem;
    }
`;

export const ReconnectNoticeTitle = styled.span`
    color: ${theme.colors.text.primary};
    font-weight: 600;
`;

export const ReconnectNoticeText = styled.p`
    color: ${theme.colors.text.primary};
    font-size: 0.8rem;
    line-height: 1.35;
    margin: 0;
`;

export const ReconnectNoticeDismissButton = styled.button`
    align-self: flex-end;
    background: transparent;
    border: 0;
    color: ${theme.colors.text.blueLink};
    cursor: pointer;
    font-size: 0.75rem;
    font-weight: 600;
    margin-top: 2px;
    padding: 2px 4px;
    text-decoration: underline;

    &:hover {
        color: ${theme.colors.text.primary};
    }

    &:focus-visible {
        outline: 2px solid ${theme.colors.border.inputHover};
        outline-offset: 1px;
    }
`;
